import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  getRunEvents, getRunTrail, listInventoryMaps, listStorageLocations,
  type AisleCoverage, type MapFrameTransform,
} from "../api/rest";
import { CollapsibleSection } from "../app/layout/CollapsibleSection";
import { IDENTITY, boundsOf, fitView, worldToScreen, type Bounds, type Point } from "./mapCalibration";
import { buildTimeline, trailLengthM, trailSegments, type TrailSegment } from "./runTrail";
import css from "./RunTrail.module.css";

interface Props {
  siteId: string;
  warehouseId: string;
  runId: string;
  /** The page's own per-aisle rollup — the timeline's milestones come from the
   *  same fetch the aisle table renders, so the two can never disagree. */
  aisles: AisleCoverage[];
  mapName: string | null;
  /** The site's IANA zone — the clock this run's times mean (ADR 265).
   *  Undefined while the site list loads; the viewer's zone stands in. */
  timeZone?: string;
}

/**
 * ADR 259 — "TRILHA DO iC3" + "LINHA DO TEMPO": where the kit went during the run,
 * and what happened while it was there.
 *
 * This is the first reader of the two telemetry planes slice B landed (ADR 248).
 * Its point is evidence: a coverage percentage is a number you have to trust, and a
 * trail is the same fact you can see — including the aisle the kit never entered.
 */
export function RunTrailSection({ siteId, warehouseId, runId, aisles, mapName, timeZone }: Props) {
  const { t, i18n } = useTranslation();

  const trailQuery = useQuery({
    queryKey: ["run-trail", siteId, warehouseId, runId],
    queryFn: () => getRunTrail(siteId, warehouseId, runId),
    retry: 0,
  });
  const eventsQuery = useQuery({
    queryKey: ["run-events", siteId, warehouseId, runId],
    queryFn: () => getRunEvents(siteId, warehouseId, runId),
    retry: 0,
  });
  const locationsQuery = useQuery({
    queryKey: ["inventory-locations", siteId, warehouseId],
    queryFn: () => listStorageLocations(siteId, warehouseId),
  });
  const mapsQuery = useQuery({
    queryKey: ["inventory-maps", siteId],
    queryFn: () => listInventoryMaps(siteId),
  });

  // The poses are in the map frame (PlaneMapper stores them unchanged); the address
  // master this draws them over is in the warehouse frame. The map's own saved
  // calibration is what reconciles them — the same transform the alignment editor
  // writes (ADR 258). Uncalibrated map ⇒ identity, which is what /live also assumes.
  const transform: MapFrameTransform = useMemo(() => {
    const map = mapsQuery.data?.find((m) => m.name === mapName);
    return map?.mapToWarehouse ?? IDENTITY;
  }, [mapsQuery.data, mapName]);

  const trail = trailQuery.data;
  const segments = useMemo(
    () => (trail ? trailSegments(trail.points, transform, trail.bucketMs) : []),
    [trail, transform],
  );

  /** Address centroids, the floor plan this trail is read against. */
  const floor: Point[] = useMemo(() => {
    const rows = locationsQuery.data ?? [];
    const out: Point[] = [];
    for (const l of rows) {
      if (!l.polygon || l.polygon.length === 0) continue;
      let sx = 0, sy = 0, n = 0;
      for (const [x, y] of l.polygon) {
        if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
        sx += x; sy += y; n++;
      }
      if (n > 0) out.push([sx / n, sy / n]);
    }
    return out;
  }, [locationsQuery.data]);

  const timeline = useMemo(
    () => buildTimeline(eventsQuery.data?.events ?? [], aisles),
    [eventsQuery.data, aisles],
  );

  const [canvasEl, setCanvasEl] = useState<HTMLCanvasElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  // Callback ref, not an effect: the canvas mounts behind a loading branch, so an
  // effect with a ref would read null once and never run again — a perfectly healthy
  // page over a blank canvas, which no unit test can see (jsdom has no 2-D context).
  const canvasRef = useCallback((node: HTMLCanvasElement | null) => {
    setCanvasEl(node);
    const parent = node?.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    setSize({ width: Math.floor(rect.width), height: Math.floor(rect.height) });
  }, []);

  useEffect(() => {
    const parent = canvasEl?.parentElement;
    // Optional, like the 2-D context below: this section is one panel of a long report,
    // and a missing browser API must degrade to "no live resize", never take the page
    // down with it. The callback ref has already measured once by here.
    if (!parent || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width: Math.floor(width), height: Math.floor(height) });
    });
    ro.observe(parent);
    return () => ro.disconnect();
  }, [canvasEl]);

  const bounds: Bounds | null = useMemo(() => {
    // Fit the floor AND the trail: a trail that left the address master (a kit that
    // walked the dock) must stay on screen, not be cropped into looking complete.
    const all: Point[][] = [floor, ...segments.map((s) => s.points)];
    return boundsOf(all.filter((p) => p.length > 0));
  }, [floor, segments]);

  const view = useMemo(() => fitView(bounds, size.width, size.height, 16), [bounds, size]);

  useEffect(() => {
    const el = canvasEl;
    if (!el || size.width === 0) return;
    const dpr = window.devicePixelRatio || 1;
    el.width = size.width * dpr;
    el.height = size.height * dpr;
    const ctx = el.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.width, size.height);

    const style = getComputedStyle(el);
    const token = (name: string, fallback: string) =>
      style.getPropertyValue(name).trim() || fallback;

    // Floor: one path for every address, so a 15k-bin warehouse is one draw call.
    ctx.fillStyle = token("--op-line", "#DCE3F1");
    ctx.beginPath();
    for (const [x, y] of floor) {
      const [px, py] = worldToScreen(view, x, y);
      ctx.moveTo(px + 1.8, py);
      ctx.arc(px, py, 1.8, 0, Math.PI * 2);
    }
    ctx.fill();

    for (const seg of segments) {
      ctx.strokeStyle = seg.localized ? token("--op-blue-500", "#1E5BD8") : token("--op-warn", "#D97706");
      ctx.lineWidth = seg.localized ? 2 : 1.5;
      ctx.setLineDash(seg.localized ? [] : [4, 3]);   // a guess is drawn as a guess
      ctx.lineJoin = "round";
      ctx.beginPath();
      seg.points.forEach((p, i) => {
        const [px, py] = worldToScreen(view, p[0], p[1]);
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      });
      ctx.stroke();
    }
    ctx.setLineDash([]);

    const ends = endpoints(segments);
    if (ends) {
      dot(ctx, worldToScreen(view, ends.start[0], ends.start[1]), token("--op-success", "#16A34A"));
      dot(ctx, worldToScreen(view, ends.end[0], ends.end[1]), token("--op-blue-900", "#05122E"));
    }
  }, [canvasEl, size, view, floor, segments]);

  // ADR 410: the date is always on the entry. It used to appear only when the walk
  // spanned a day, and two reports of the same warehouse then looked inconsistent
  // ("one shows dates, the other only times") to the people comparing them.
  // The warehouse's clock, not the viewer's (ADR 265). "Corredor JY04 concluído
  // 15:30" is a statement about the floor; rendering it in the zone of whoever
  // opened the page makes it true of nothing.
  const fmtTime = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
      ...(timeZone ? { timeZone } : {}),
    }),
    [i18n.language, timeZone],
  );

  const hasTrail = segments.length > 0;
  const assetKnown = trail?.assetKnown ?? false;

  // ADR 410: a run with neither poses nor a timeline has no section, and a run with
  // a timeline but no poses shows the timeline alone — a box saying "no trail" is
  // still an empty box on a customer's report. Nothing renders until the trail has
  // answered, so the pane never appears and then folds.
  if (trailQuery.isLoading) return null;
  if (!hasTrail && timeline.length === 0) return null;

  return (
    <CollapsibleSection id="run.trail" title={t("inventory.trail.title")} data-testid="trail-section">
      <div className={hasTrail ? css.layout : css.timelineOnly}>
        {hasTrail && (
          <div>
            <div className={css.canvasWrap}>
              <canvas ref={canvasRef} className={css.canvas} data-testid="trail-canvas" />
            </div>
            <div className={css.legend}>
              <span><i className={css.swatchTrail} />{t("inventory.trail.legend.localized")}</span>
              <span><i className={css.swatchLost} />{t("inventory.trail.legend.unlocalized")}</span>
              <span><i className={css.swatchStart} />{t("inventory.trail.legend.start")}</span>
              <span><i className={css.swatchEnd} />{t("inventory.trail.legend.end")}</span>
            </div>
            <p className={css.note} data-testid="trail-stats">
              {t("inventory.trail.stats", {
                meters: trailLengthM(segments).toLocaleString(i18n.language, { maximumFractionDigits: 0 }),
                points: trail?.points.length ?? 0,
              })}
            </p>
          </div>
        )}

        <div className={css.timeline} data-testid="run-timeline">
          {!assetKnown && (
            <p className={css.note} data-testid="timeline-unlinked">{t("inventory.trail.unlinked")}</p>
          )}
          {assetKnown && timeline.length === 0 && (
            <p className={css.note}>{t("inventory.trail.noEvents")}</p>
          )}
          <ol className={css.entries}>
            {timeline.map((e, i) => (
              <li key={`${e.ts}-${e.type}-${e.label}-${i}`} className={css.entry}>
                <span className={`${css.bullet} ${css[e.tone]}`} aria-hidden="true" />
                <time className={css.time} dateTime={new Date(e.ts).toISOString()}>
                  {fmtTime.format(new Date(e.ts))}
                </time>
                <span className={css.label}>
                  {e.type === "event"
                    ? t(`inventory.trail.event.${e.label}`, { defaultValue: e.label })
                    : t(`inventory.trail.${e.type}`, { aisle: e.label })}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </CollapsibleSection>
  );
}

/** First and last point actually walked, across segment breaks. */
function endpoints(segments: readonly TrailSegment[]): { start: Point; end: Point } | null {
  if (segments.length === 0) return null;
  const first = segments[0].points[0];
  const lastSeg = segments[segments.length - 1].points;
  return { start: first, end: lastSeg[lastSeg.length - 1] };
}

function dot(ctx: CanvasRenderingContext2D, [px, py]: Point, color: string) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(px, py, 4, 0, Math.PI * 2);
  ctx.fill();
}

export default RunTrailSection;
